import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner,
} from 'react-bootstrap';
import {
  FaPlus, FaSearch, FaWpforms, FaLayerGroup, FaListUl, FaInbox,
  FaToggleOn, FaToggleOff, FaEdit, FaTrash,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  getForms, createForm, deleteForm,
  type FormSummary, type CreateFormRequest,
} from '@/api/formsManagementApi';
import './FormsManagement.scss';

const FormsManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const [forms, setForms] = useState<FormSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createReq, setCreateReq] = useState<CreateFormRequest>({ formTitle: '' });
  const [creating, setCreating] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<FormSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setForms(await getForms(includeInactive));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = forms.filter(f => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      f.formTitle.toLowerCase().includes(q) ||
      (f.formDescription ?? '').toLowerCase().includes(q)
    );
  });

  const handleCreate = async () => {
    if (!createReq.formTitle.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const { formGuid } = await createForm(createReq);
      setShowCreate(false);
      setCreateReq({ formTitle: '' });
      setSuccess(`Form created successfully.`);
      setTimeout(() => setSuccess(null), 4000);
      navigate(`/admin/forms/${formGuid}`);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteForm(deleteTarget.formGuid);
      setSuccess(`"${deleteTarget.formTitle}" deactivated.`);
      setDeleteTarget(null);
      setTimeout(() => setSuccess(null), 4000);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to deactivate form');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="forms-management container-fluid p-4">
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            <FaWpforms className="text-primary" />
            Forms Management
          </h4>
          <p className="text-muted mb-0 small">Create and manage form definitions, sections, fields, and submissions.</p>
        </div>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Check
            type="switch"
            id="inactive-toggle"
            label={includeInactive ? <><FaToggleOn className="me-1" />Showing all</> : <><FaToggleOff className="me-1" />Active only</>}
            checked={includeInactive}
            onChange={e => setIncludeInactive(e.target.checked)}
          />
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            <FaPlus className="me-1" />
            New Form
          </Button>
        </div>
      </div>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Search */}
      <InputGroup className="mb-4" style={{ maxWidth: 420 }}>
        <InputGroup.Text><FaSearch /></InputGroup.Text>
        <Form.Control
          placeholder="Search by title or description…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <Button variant="outline-secondary" onClick={() => setSearch('')}>Clear</Button>}
      </InputGroup>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted py-5">
          <FaInbox size={32} className="mb-2 opacity-50" />
          <p>No forms found.</p>
        </div>
      ) : (
        <>
          <p className="text-muted small mb-3">Showing {filtered.length} of {forms.length} forms</p>
          <Row className="g-3">
            {filtered.map(f => (
              <Col key={f.formGuid} xs={12} sm={6} lg={4} xl={3}>
                <Card
                  className="form-card h-100"
                  onClick={() => navigate(`/admin/forms/${f.formGuid}`)}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <FaWpforms className="form-card-icon mt-1" />
                      <div className="d-flex gap-1">
                        {!f.isActive && <Badge bg="secondary">Inactive</Badge>}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          title="Edit form"
                          onClick={e => { e.stopPropagation(); navigate(`/admin/forms/${f.formGuid}`); }}
                        >
                          <FaEdit />
                        </Button>
                        {f.isActive && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Deactivate form"
                            onClick={e => { e.stopPropagation(); setDeleteTarget(f); }}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="fw-semibold mb-1" style={{ wordBreak: 'break-word' }}>
                      {f.formTitle}
                    </div>
                    {f.formDescription && (
                      <div className="text-muted small mb-2" style={{
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {f.formDescription}
                      </div>
                    )}

                    <div className="form-card-stats d-flex mt-auto pt-2 border-top">
                      <span title="Sections"><FaLayerGroup className="me-1" />{f.sectionCount}</span>
                      <span title="Fields"><FaListUl className="me-1" />{f.fieldCount}</span>
                      <span title="Submissions"><FaInbox className="me-1" />{f.submissionCount}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Create Form Modal */}
      <Modal show={showCreate} onHide={() => { setShowCreate(false); setCreateReq({ formTitle: '' }); }}>
        <Modal.Header closeButton>
          <Modal.Title><FaWpforms className="me-2" />New Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Form Title <span className="text-danger">*</span></Form.Label>
              <Form.Control
                autoFocus
                type="text"
                placeholder="e.g. Compressed Summer Work Week Application"
                value={createReq.formTitle}
                onChange={e => setCreateReq(r => ({ ...r, formTitle: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Brief description of the form's purpose"
                value={createReq.formDescription ?? ''}
                onChange={e => setCreateReq(r => ({ ...r, formDescription: e.target.value || undefined }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowCreate(false); setCreateReq({ formTitle: '' }); }} disabled={creating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreate} disabled={creating || !createReq.formTitle.trim()}>
            {creating ? <Spinner size="sm" animation="border" className="me-1" /> : <FaPlus className="me-1" />}
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deactivate Confirm Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Deactivate <strong>{deleteTarget?.formTitle}</strong>?</p>
          <p className="text-muted small mb-0">
            The form and all its sections, fields, and options will be soft-deleted. Existing submissions are preserved.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" animation="border" className="me-1" /> : <FaTrash className="me-1" />}
            Deactivate
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FormsManagementPage;
